import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import {
  GetWorkSpaceSlotsDocument,
  WorkSpaceSlotFragment,
  WorkSpaceUpdatedSubscriptionDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { FormErrors, FormProvider } from 'modules/core/hooks/useForm'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'
import app from 'modules/core/store/models/app'

import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'
import BookingSidepanel from 'modules/space_control/components/BookingsSidepanel/BookingSidepanel'
import { useBooking } from 'modules/space_control/hooks/bookings/useBooking'
import { useBulkEditBooking } from 'modules/space_control/hooks/bookings/useBulkEditBooking'
import { WorkSpaceNode } from 'modules/space_control/hooks/useWorkSpaces'

interface bookingFormFields {
  accessId: number
  date: string
  slots: number[]
}

const EditBookingSidepanelPage = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { bookingId } = useParams()
  const { booking, loading: isBookingLoading } = useBooking(Number(bookingId))
  const bookingDate = booking?.date ?? dateToString(moment(), 'backend')
  const currentAccessId = users.current?.id
  const [currentDate, setCurrentDate] = useState<string>(bookingDate)
  const workSpace = booking?.workSpace
  const [workSpaceSlots, setWorkSpaceSlots] = useState<WorkSpaceSlotFragment[]>([])
  const [defaultSlots, setDefaultSlots] = useState<number[]>([])
  const updateVerb = 'spacecontrol.booking.upsert'
  useAsyncData(
    () =>
      policyAuthorizations.authorizePolicies(
        updateVerb,
        users.map((user) => ({
          id: user.id,
          policyTargetClass: '::Access',
        }))
      ),
    []
  )
  useEffect(() => {
    setCurrentDate(bookingDate)
  }, [bookingDate])

  const { loading: loadingSlots, refetch: slotsRefetch } = useQuery(GetWorkSpaceSlotsDocument, {
    variables: {
      workSpaceId: Number(booking?.workSpace?.id),
      date: currentDate,
    },
    skip: !workSpace?.id,
    onCompleted: (data) => {
      const _workSpaceSlots = data.spaceControl.workSpaceSlotsConnection.nodes
      setWorkSpaceSlots(_workSpaceSlots || [])
      setDefaultSlots(
        _workSpaceSlots
          .filter(
            (slot) => slot.bookingIds.length !== 0 && slot.bookingIds.includes(Number(bookingId))
          )
          .map((slot) => slot.slotNumber)
      )
    },
  })
  useSubscription(WorkSpaceUpdatedSubscriptionDocument, {
    variables: { id: workSpace?.id },
    skip: !workSpace?.id,
    onData: (data) => {
      if (Object.keys(data.data).length < 1) return

      slotsRefetch()
    },
  })

  const slotSetup = workSpace?.slotSetup

  const onClose = () => {
    track(trackingEvents.spaceControl.bookings.cancelBooking)
    navigate(navigation.spaceControl.bookings.fullPath())
  }

  const defaultValues = {
    accessId: booking?.access?.id as number,
    date: dateToString(moment(currentDate), 'ui'),
    slots: defaultSlots,
  }

  const getBookedSlots = useMemo(() => {
    if (!workSpace?.allowMultipleBookings) {
      return workSpaceSlots.filter(
        (slot) => slot.bookingIds.length !== 0 && !slot.bookingIds.includes(Number(bookingId))
      )
    } else {
      return workSpaceSlots.filter((slot) => slot.currentCapacity === 0)
    }
  }, [bookingId, workSpace?.allowMultipleBookings, loadingSlots, currentDate])

  const { updateSpacecontrolBooking } = useBulkEditBooking({})

  const setFlashMessage = (success: boolean) => {
    const type = success ? 'correct' : 'wrong'
    const message = success
      ? i18n.t('space_control.overview.booking_sidepanel.submit_booking.success')
      : i18n.t('space_control.overview.booking_sidepanel.submit_booking.error')

    app.setFlash({
      type: type,
      message: message,
      time: 2000,
    })
  }

  if (booking && !isBookingLoading)
    return (
      <FormProvider
        enableReinitialize
        defaultValues={defaultValues}
        validate={({ date, accessId, slots }) => {
          const errors: FormErrors<bookingFormFields> = {}

          if (!date) {
            errors.date = i18n.t('common.empty')
          }
          if (!accessId) {
            errors.accessId = i18n.t('common.empty')
          }
          if (slotSetup !== 'whole_day' && (!slots || slots.length === 0)) {
            errors.slots = i18n.t('common.empty')
          }

          return errors
        }}
        onSubmit={async (values, { setErrors }) => {
          const date = stringToDate(values.date, 'ui')
          const result = await updateSpacecontrolBooking({
            ...values,
            date: dateToString(date, 'backend'),
            ids: [Number(bookingId)],
            slots: values.slots,
            workSpaceId: workSpace?.id ?? 0,
          })
          const handled = handleResult(result, (errors) => {
            if (errors) {
              setErrors(errors)
            }
          })

          setFlashMessage(handled.success)

          if (handled.success) {
            track(trackingEvents.spaceControl.bookings.editBooking, {
              type_space: workSpace?.workSpaceTypeValue,
            })
            navigate(navigation.spaceControl.bookings.fullPath())
          }
          return handled
        }}
      >
        <BookingSidepanel
          workSpace={workSpace as WorkSpaceNode}
          onClose={onClose}
          slots={workSpaceSlots}
          bookedSlots={getBookedSlots}
          isSlotsLoading={loadingSlots}
          onChangeDate={(date) => setCurrentDate(date)}
          edit
          accesses={users.filter(
            (access) =>
              access.invited &&
              ((access.employee && policyAuthorizations.for(updateVerb, access.employee)) ||
                (users.current.policy.can('spaceControl.bookingEditSelf') &&
                  access.id == currentAccessId) ||
                users.current.policy.can('spaceControl.bookingEditEverybody'))
          )}
        />
      </FormProvider>
    )
  return <></>
}

export default EditBookingSidepanelPage
